/**
 * The API's error envelope, as a typed error.
 *
 * Every failure from /api/v1 arrives as
 * `{ error: { code, message, status, requestId, details? } }`, so the whole
 * application can branch on `code` instead of parsing messages. `status` is a
 * public field because the query client reads it to decide whether retrying
 * could possibly help.
 */

/** Field errors from a 422, keyed by the field name the request used. */
export type FieldErrors = Record<string, string[]>;

export type ApiErrorShape = {
  code: string;
  message: string;
  status: number;
  requestId?: string;
  details?: FieldErrors | Record<string, unknown>;
};

/** Status used when the request never reached the server at all. */
export const NETWORK_STATUS = 0;

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId?: string;
  readonly details?: FieldErrors | Record<string, unknown>;

  constructor(shape: ApiErrorShape) {
    super(shape.message);
    this.name = 'ApiError';
    this.code = shape.code;
    this.status = shape.status;
    this.requestId = shape.requestId;
    this.details = shape.details;
  }

  /** A 422's field errors, or an empty object for any other failure. */
  fieldErrors(): FieldErrors {
    if (this.status !== 422 || !this.details) return {};
    return Object.fromEntries(
      Object.entries(this.details)
        .filter(([, messages]) => Array.isArray(messages))
        .map(([field, messages]) => [field, messages as string[]]),
    );
  }

  /** The request never got a reply: offline, DNS, CORS, a dropped connection. */
  get isNetworkFailure(): boolean {
    return this.status === NETWORK_STATUS;
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;
